import type { ParagraphAncestor } from './indexParagraphs';
import { wrapSection } from '../serialize/serializeCast';
import type { SerializeOptions } from '../serialize/serializeOptions';
import { serializeParagraph } from '../serialize/serializeParagraph';
import type { CastParagraph } from '../types';

export type EstimateTokens = (text: string) => number;

/**
 * Serializes a single paragraph as if it were the only thing being emitted,
 * regardless of its current `included` flag, then runs it through the
 * token estimator. Mirrors what the paragraph will actually contribute when
 * the wrapping section/table/etc. eventually emits.
 */
export function estimateParagraphCost(
  paragraph: CastParagraph,
  serializeOptions: SerializeOptions,
  estimateTokens: EstimateTokens
): number {
  const probe: CastParagraph = { ...paragraph, included: true };
  const serialized = serializeParagraph(probe, serializeOptions) ?? '';
  return estimateTokens(serialized);
}

/**
 * Estimates the token overhead a container's open/close tags add when it emits
 * content: the tag pair plus the two newlines that bracket the content, i.e. the
 * wrapper with empty inner (`<tag>\n\n</tag>`).
 *
 * Sections go through `wrapSection` — the same code the serializer uses — so the
 * estimate tracks their `variant` and font attributes exactly and can't drift.
 * Table/row/cell tags are attribute-free, so a constant is already exact.
 */
export function estimateWrapperCost(
  ancestor: ParagraphAncestor,
  serializeOptions: SerializeOptions,
  estimateTokens: EstimateTokens
): number {
  switch (ancestor.type) {
    case 'section':
      return estimateTokens(wrapSection(ancestor, serializeOptions, ''));
    case 'table':
      return estimateTokens('<table>\n\n</table>');
    case 'tableRow':
      return estimateTokens('<tr>\n\n</tr>');
    case 'tableCell':
      return estimateTokens('<td>\n\n</td>');
  }
}
